import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Form,
  FormFeedback,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import * as Yup from "yup";
import "react-toastify/dist/ReactToastify.css";

import {
  ActivityButton,
  SequenceNumberInput,
} from "../../../components/Common";

import { ErrorHandlingTypeEnum, SequenceEnum } from "../../../enums";
import { CreateAssignmentRequest } from "../../../interfaces/lecture/assignment";
import {
  useGetLectureAssignmentQuery,
  useAddLectureAssignmentMutation,
  useUpdateLectureAssignmentMutation,
} from "../../../store/api/lectureAssignmentApi";
import { ParsedError } from "../../../interfaces";

type TProps = {
  id?: string | null;
  show: boolean;
  onCloseClick: (event: any) => void;
  lectureId: string;
};
export const AddEditLectureAssignmentModal: React.FC<TProps> = ({
  id,
  show,
  onCloseClick,
  lectureId,
}) => {
  const { t } = useTranslation();
  const [request] = useState<CreateAssignmentRequest>();

  const [
    addLectureAssignment,
    {
      isLoading: isLoadingAdd,
      isSuccess: isSuccessAdd,
      isError: isErrorAdd,
      error: errorAdd,
    },
  ] = useAddLectureAssignmentMutation();
  const [
    updateLectureAssignment,
    {
      isLoading: isLoadingUpdate,
      isSuccess: isSuccessUpdate,
      isError: isErrorUpdate,
      error: errorUpdate,
    },
  ] = useUpdateLectureAssignmentMutation();

  const {
    data: dataAssignment,
    error: errorAssignment,
    isLoading: isLoadingAssignment,
    isError: isErrorAssignment,
  } = useGetLectureAssignmentQuery(id as string, { skip: !id });

  //#region form
  const form = useFormik<CreateAssignmentRequest>({
    enableReinitialize: true,
    initialValues: {
      title: {
        en: request?.title.en || "",
        ar: request?.title.ar || "",
      },
      sequenceNumber: request?.sequenceNumber || 0,
    },
    validationSchema: Yup.object({
      title: Yup.object({
        en: Yup.string()
          .max(
            100,
            t("maxLength", { maxLength: 100, displayName: "englishValue" }),
          )
          .required(
            t("isNotEmpty", {
              displayName: "englishValue",
            }),
          ),
        ar: Yup.string()
          .max(
            100,
            t("maxLength", { maxLength: 100, displayName: "englishValue" }),
          )
          .required(
            t("isNotEmpty", {
              displayName: "arabicValue",
            }),
          ),
      }),
      sequenceNumber: Yup.number().required(
        t("isNotEmpty", {
          displayName: "sequenceNumber",
        }),
      ),
    }),
    onSubmit: async (values) => {
      try {
        if (id) {
          await updateLectureAssignment({
            id,
            data: { ...values, sequenceNumber: Number(values.sequenceNumber) },
          }).unwrap();
        } else {
          await addLectureAssignment({
            lectureId,
            data: { ...values, sequenceNumber: Number(values.sequenceNumber) },
          }).unwrap();
        }
        onClose();
      } catch (err) {}
    },
  });
  //#endregion

  //#region handlers
  const onClose = () => {
    form?.resetForm();
    onCloseClick(!show);
  };
  //#endregion

  //#region effects
  useEffect(() => {
    if (id && dataAssignment) {
      form.setValues({
        title: dataAssignment.title,
        sequenceNumber:
          dataAssignment?.sequenceNumber || form.values.sequenceNumber || 0,
      });
    }
  }, [dataAssignment, isLoadingAssignment, id]);

  useEffect(() => {
    let _error: ParsedError | null = null;
    if (!isLoadingAdd && isErrorAdd) _error = errorAdd as ParsedError;
    if (!isLoadingUpdate && isErrorUpdate) _error = errorUpdate as ParsedError;
    if (_error?.type == ErrorHandlingTypeEnum.FORM) {
      form.setErrors(_error.error);
    }
  }, [isLoadingAdd, isLoadingUpdate]);

  useEffect(() => {
    if (
      (!isLoadingAdd && isSuccessAdd) ||
      (!isLoadingUpdate && isSuccessUpdate)
    ) {
      onClose();
    }
  }, [isLoadingAdd, isSuccessAdd, isLoadingUpdate, isSuccessUpdate]);

  //#endregion

  return (
    <React.Fragment>
      <Modal
        id="addEdit"
        isOpen={show}
        toggle={() => {
          onClose();
        }}
        centered
        className="modal-lg"
      >
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
            return false;
          }}
          action="#"
        >
          <ModalHeader
            className="p-3"
            toggle={() => {
              onClose();
            }}
          >
            {id ? t("EditAssignment") : t("AddAssignment")}
          </ModalHeader>

          <ModalBody>
            <SequenceNumberInput
              tabIndex={1}
              value={form.values.sequenceNumber}
              onChange={(e) => {
                e.preventDefault();
                const { value } = e.target;
                const regex =
                  /^(0*[0-9][0-9]*(\.[0-9]*)?|0*\.[0-9]*[1-9][0-9]*)$/;
                if (regex.test(value.toString())) {
                  form.setFieldValue("sequenceNumber", value);
                } else {
                  form.setFieldValue("sequenceNumber", "");
                }
              }}
              onBlur={form.handleBlur}
              invalid={
                form.touched.sequenceNumber && form.errors.sequenceNumber
                  ? true
                  : false
              }
              sequenceType={SequenceEnum.LECTURE_ASSIGNMENT}
              onGenerateSequenceNumber={(v: number) => {
                form.setFieldValue("sequenceNumber", v);
              }}
              mode={id ? "edit" : "add"}
              request={{
                type: SequenceEnum.LECTURE_ASSIGNMENT,
                lectureId: lectureId,
              }}
            />
            {form.touched.sequenceNumber && form.errors.sequenceNumber ? (
              <FormFeedback type="invalid">
                {form.errors.sequenceNumber as string}
              </FormFeedback>
            ) : null}
            <div className="mb-3">
              <label htmlFor="title.en" className="form-label">
                {t("AssignmentTopicInEnglish")}
                <span className="text-danger">*</span>
              </label>
              <Input
                autoComplete="off"
                id="title.en"
                name="title.en"
                value={form.values?.title.en}
                type="text"
                className="form-control"
                placeholder={t("enterInEnglish")}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                invalid={
                  form.touched.title?.en && form.errors.title?.en ? true : false
                }
              />
              {form.touched.title?.en && form.errors.title?.en ? (
                <FormFeedback type="invalid">
                  {form.errors.title?.en as string}
                </FormFeedback>
              ) : null}
            </div>
            <div className="mb-3">
              <label htmlFor="title.ar" className="form-label">
                {t("AssignmentTopicInArabic")}
                <span className="text-danger">*</span>
              </label>
              <Input
                style={{ fontFamily: "aljannat", direction: "rtl" }}
                autoComplete="off"
                auto
                id="title.ar"
                name="title.ar"
                value={form.values.title?.ar}
                type="text"
                className="form-control"
                placeholder={t("enterInArabic")}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                invalid={
                  form.touched.title?.ar && form.errors.title?.ar ? true : false
                }
              />
              {form.touched.title?.ar && form.errors.title?.ar ? (
                <FormFeedback type="invalid">
                  {form.errors.title?.ar as string}
                </FormFeedback>
              ) : null}
            </div>
          </ModalBody>
          <ModalFooter>
            <Link
              to={"#"}
              onClick={onClose}
              className="btn btn-link link-success fw-medium"
            >
              <i className="ri-close-line me-1 align-middle"></i> {t("close")}
            </Link>
            <ActivityButton
              title="save"
              inProgressTitle="saving"
              color="success"
              className="btn btn-success"
              type="submit"
              inProgress={isLoadingAdd || isLoadingUpdate}
              disabled={isLoadingAdd || isLoadingUpdate || isErrorAssignment}
            ></ActivityButton>
          </ModalFooter>
        </Form>
      </Modal>
    </React.Fragment>
  );
};
